import React, { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_API_URL;

const CATEGORIES = [
  { id: "GRATITUDE",   label: "Gratitude",   color: "bg-emerald-500 hover:bg-emerald-600", icon: "🟢" },
  { id: "NEUTRAL",     label: "Neutre",       color: "bg-slate-500 hover:bg-slate-600",     icon: "🟡" },
  { id: "FRUSTRATION", label: "Frustration",  color: "bg-orange-500 hover:bg-orange-600",   icon: "🟠" },
  { id: "TOXICITY",    label: "Toxicité",     color: "bg-red-500 hover:bg-red-600",         icon: "🔴" },
];

const SUPERVISOR_TIMEOUT_MS = 8000;

export default function TrainingClassifier({ sessionCode, onComplete }) {
  const [active, setActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const completedRef = useRef(false);
  const currentBatchRef = useRef(null);
  const [timeBoostActive, setTimeBoostActive] = useState(false);

  // ── Superviseur — ref pour éviter de re-trigger le timer ─────────────────
  const [supervisorStatus, setSupervisorStatus] = useState("unknown");
  const supervisorStatusRef = useRef("unknown"); // utilisé dans le timer sans créer de dep

  const updateSupervisorStatus = (status) => {
    supervisorStatusRef.current = status;
    setSupervisorStatus(status);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Polling principal
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${sessionCode}/training/state`);
        const data = await res.json();

        if (data.completed) {
          updateSupervisorStatus("completed");
        } else if (data.active) {
          if (data.supervisorLastSeen) {
            const age = Date.now() - new Date(data.supervisorLastSeen).getTime();
            updateSupervisorStatus(age < SUPERVISOR_TIMEOUT_MS ? "present" : "absent");
          } else {
            updateSupervisorStatus("present");
          }
        } else {
          updateSupervisorStatus("absent");
        }

        if (data.active && !active) {
          setActive(true);
          setTimeBoostActive(data.timeBoostActive || false);
          currentBatchRef.current = data.currentBatch;
        } else if (!data.active && active && !data.completed) {
          setActive(false);
        }

        if (data.active && active && data.currentBatch) {
          const serverBatch = data.currentBatch;
          if (currentBatchRef.current && currentBatchRef.current !== serverBatch) {
            currentBatchRef.current = serverBatch;
            setCurrentIndex(0);
          } else if (!currentBatchRef.current) {
            currentBatchRef.current = serverBatch;
          }
        }

        if (data.completed && !completedRef.current) {
          completedRef.current = true;
          setCompleted(true);
          setActive(false);
          if (data.unlockCode) { onComplete?.(data); }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [sessionCode, active, onComplete]);

  // Time boost
  useEffect(() => {
    if (timeBoostActive && active && currentMessage && timeLeft > 0) {
      setTimeLeft(t => t + 2);
      setTimeBoostActive(false);
    }
  }, [timeBoostActive, active, currentMessage]);

  // Charger messages
  useEffect(() => {
    if (!active) return;
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${sessionCode}/training/messages`);
        const data = await res.json();
        if (data.messages && data.messages.length > 0) setMessages(data.messages);
      } catch (error) { console.error("Error fetching messages:", error); }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [sessionCode, active]);

  // Current message — reset timeLeft quand le message change
  useEffect(() => {
    if (active && messages.length > 0) {
      const msg = messages[currentIndex % messages.length];
      setCurrentMessage(msg);
      setTimeLeft(msg.duration / 1000);
      setFeedback(null);
    }
  }, [active, currentIndex, messages]);

  // classify via ref pour éviter les deps circulaires dans le timer
  const handleClassify = useCallback(async (category) => {
    if (loading || !currentMessage) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sessions/${sessionCode}/training/classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messageIndex: currentIndex, category: category || "TIMEOUT" }),
      });
      const data = await res.json();
      setFeedback(data.correct ? "correct" : "wrong");
      setTimeout(() => { setCurrentIndex(data.nextMessageIndex); setFeedback(null); setLoading(false); }, 600);
    } catch (error) { console.error("Classify error:", error); setLoading(false); }
  }, [loading, currentMessage, sessionCode, currentIndex]);

  // ── Timer — supervisorStatus lu via ref, pas dans les deps ───────────────
  // Cela évite que le changement de supervisorStatus re-crée l'interval
  // et remette le compteur à 0.
  useEffect(() => {
    if (!active || timeLeft <= 0 || feedback || !currentMessage) return;

    const timer = setInterval(() => {
      // Lire le statut superviseur via ref : pas de re-render, pas de re-création du timer
      if (supervisorStatusRef.current === "absent") return;

      setTimeLeft(t => {
        if (t <= 0.1) {
          handleClassify(null);
          return 0;
        }
        return t - 0.1;
      });
    }, 100);

    return () => clearInterval(timer);
  // supervisorStatus intentionnellement ABSENT des deps — on lit via ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, feedback, currentMessage, handleClassify]);
  // ─────────────────────────────────────────────────────────────────────────

  // Bandeau superviseur
  const SupervisorBanner = () => {
    if (supervisorStatus === "unknown" || supervisorStatus === "completed") return null;
    if (supervisorStatus === "present") {
      return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-xs font-mono text-emerald-300">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
          Superviseur connecté — Salle de Supervision IA
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-400/50 bg-amber-500/10">
        <span className="text-amber-400 text-lg flex-shrink-0 mt-0.5">⏳</span>
        <div className="text-xs text-amber-200 leading-relaxed">
          <span className="font-semibold block mb-0.5">
            {active ? "Superviseur déconnecté — classification en pause" : "En attente du superviseur"}
          </span>
          {active
            ? "Votre coéquipier a fermé le moniteur de calibration. Demandez-lui de le rouvrir pour continuer."
            : <>Votre coéquipier doit initialiser la séquence depuis la{" "}<span className="font-semibold text-amber-100">Salle de Supervision IA</span>{" "}pour que les messages s'affichent.</>
          }
        </div>
      </div>
    );
  };

  // Écran succès
  if (completed) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950/30 border border-emerald-500/30 p-8 min-h-[500px] flex flex-col justify-center">
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(rgba(52,211,153,1) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,1) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }} />
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(52,211,153,0.15) 0%, transparent 65%)" }} />
        <div className="relative space-y-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-[11px] font-mono text-emerald-400 uppercase tracking-[0.2em]">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Session terminée
          </div>
          <div>
            <div className="text-4xl font-bold text-white font-mono tracking-tight">
              MODULE<span className="text-emerald-400">_</span>EMPATHIE
            </div>
            <div className="text-sm text-emerald-300 font-mono mt-1">Calibration validée — 4/4 lots</div>
          </div>
          <div className="rounded-xl border border-emerald-500/40 bg-black/40 p-5 text-left">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-3">🔓 Code d'accès généré</div>
            <div className="flex items-center justify-between gap-4">
              <div className="font-mono text-2xl font-bold text-emerald-300 tracking-widest">
                EMPATH<span className="text-slate-500">-</span>4729
              </div>
              <div className="text-[10px] font-mono text-slate-500 text-right leading-relaxed">
                Dossier <span className="text-amber-400">.ingratitude</span><br />Salle des archives
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-slate-500 font-mono">
              → Communiquer ce code au joueur de la salle des archives
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2 text-center">
                <div className="text-[10px] font-mono text-emerald-400 mb-1">Lot {i}</div>
                <div className="text-emerald-400 font-bold">✓</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="min-h-[500px] p-6 space-y-6">
        <div className="p-4 rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border border-cyan-500/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-3 h-3 rounded-full bg-slate-600" />
            <div className="text-sm font-mono text-cyan-300">ORIGAMI INTERFACE ENTRAINEMENT</div>
          </div>
          <div className="text-xs text-slate-400 font-mono">&gt; Classification empathique — Module opérateur</div>
        </div>
        <SupervisorBanner />
        <div className="p-8 rounded-xl bg-slate-900/30 border border-cyan-500/10 min-h-[200px]">
          <div className="flex flex-col items-center justify-center gap-4 h-full opacity-30">
            <div className="text-6xl">💬</div>
            <div className="text-lg text-slate-500 font-mono">&gt; EN ATTENTE DE SÉQUENCE</div>
            <div className="h-2 w-64 bg-slate-800 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 opacity-30">
          {CATEGORIES.map(cat => (
            <div key={cat.id} className="px-6 py-4 rounded-xl bg-slate-800/50 border border-cyan-500/10">
              <div className="flex items-center justify-center gap-2 text-slate-600">
                <span className="text-xl">{cat.icon}</span>
                <span className="font-semibold font-mono">{cat.label}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 rounded-lg bg-blue-950/20 border border-blue-500/30">
          <div className="text-xs text-blue-200 mb-2 font-mono">&gt; Instructions</div>
          <div className="text-xs text-slate-300 space-y-1 font-mono">
            <p>• Attendez le lancement par le superviseur</p>
            <p>• Classifiez chaque message rapidement</p>
            <p>• Communiquez avec le superviseur</p>
            <p>• 4 lots de difficulté croissante</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentMessage) {
    return (
      <div className="p-8 text-center min-h-[500px] flex flex-col items-center justify-center">
        <div className="animate-spin text-4xl mb-4">⚙️</div>
        <div className="text-slate-400 font-mono">&gt; Chargement du lot...</div>
      </div>
    );
  }

  const progressPercent = (timeLeft / (currentMessage.duration / 1000)) * 100;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-cyan-500/30">
        <div className="flex items-center gap-2">
          <div className={["w-2 h-2 rounded-full",
            supervisorStatus === "absent" ? "bg-amber-400" : "bg-emerald-500 animate-pulse"
          ].join(" ")} />
          <div className="text-xs font-mono text-slate-400">
            {supervisorStatus === "absent" ? "SESSION EN PAUSE" : "SESSION ACTIVE"}
          </div>
        </div>
        <div className="text-sm font-mono text-white">{timeLeft.toFixed(1)}s</div>
      </div>

      <SupervisorBanner />

      <div className={["h-2 bg-slate-800 rounded-full overflow-hidden border border-cyan-500/20",
        supervisorStatus === "absent" ? "opacity-30" : ""
      ].join(" ")}>
        <div className={["h-full transition-all duration-100",
          progressPercent > 50 ? "bg-emerald-500" : progressPercent > 25 ? "bg-amber-500" : "bg-red-500"
        ].join(" ")} style={{ width: `${progressPercent}%` }} />
      </div>

      <div className={[
        "relative p-12 rounded-xl border-2 text-center min-h-[280px] flex items-center justify-center transition-all duration-300",
        supervisorStatus === "absent" ? "opacity-40" :
          feedback === "correct" ? "bg-emerald-950/40 border-emerald-500" :
            feedback === "wrong" ? "bg-red-950/40 border-red-500 animate-[shake_0.3s]" :
              "bg-slate-900/30 border-cyan-500/30"
      ].join(" ")}>
        <div className="text-3xl font-medium text-white leading-relaxed font-mono">{currentMessage.text}</div>
        {feedback && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-xl backdrop-blur-sm">
            <div className="text-8xl animate-pulse">{feedback === "correct" ? "✅" : "❌"}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => handleClassify(cat.id)}
            disabled={loading || feedback !== null || supervisorStatus === "absent"}
            className={[cat.color,
              "px-6 py-5 rounded-xl font-bold text-white transition-all border-2 border-transparent",
              "disabled:opacity-30 disabled:cursor-not-allowed hover:border-cyan-300/30 active:scale-95"
            ].join(" ")}>
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">{cat.icon}</span>
              <div className="text-lg font-mono">{cat.label}</div>
            </div>
          </button>
        ))}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
      `}</style>
    </div>
  );
}